import { faEdit, faGripDotsVertical, faTrash } from "@fortawesome/pro-regular-svg-icons";
import { useTranslation } from "react-i18next";
import { useDraggable } from "@dnd-kit/core";

import { Icon } from "@/components";
import { classNames } from "@/utils/classNames";

export interface PriorityDefinitionItem {
	id?: number;
	title: string;
	definition: string;
	examples?: string[];
}

interface PriorityDefinitionItemCardProps<TItem extends PriorityDefinitionItem> {
	item: TItem;
	onEdit: (item: TItem) => void;
	onDelete: (item: TItem) => void;
	/**
	 * Drag id for this card, from `priorityItemKey`. Omitted where the board is read-only; the
	 * card then renders without a drag handle. Supplied by the section, which knows the level the
	 * key needs and the card does not carry.
	 */
	dragId?: string;
}

/**
 * One priority definition.
 *
 * Dragging is bound to an explicit grip handle rather than the whole card (PREC-3908): the card
 * carries an Edit and a Delete button, and a card-wide drag listener swallows the pointer events
 * that reach them.
 */
export function PriorityDefinitionItemCard<TItem extends PriorityDefinitionItem>({
	item,
	onEdit,
	onDelete,
	dragId,
}: PriorityDefinitionItemCardProps<TItem>) {
	const { t } = useTranslation();
	const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
		id: dragId ?? item.title,
		disabled: !dragId,
	});

	return (
		<div
			ref={setNodeRef}
			className={classNames(
				"p-3 rounded-lg border border-border-light bg-white",
				isDragging ? "opacity-40" : ""
			)}
		>
			<div className="flex items-start justify-between gap-3">
				{!!dragId && (
					<button
						aria-label={t("list.signals.movePriorityItem")}
						className="mt-0.5 cursor-grab p-1 text-text-disabled hover:text-text-meta active:cursor-grabbing"
						type="button"
						{...attributes}
						{...listeners}
					>
						<Icon className="h-4 w-4" icon={faGripDotsVertical} />
					</button>
				)}
				<div className="flex-1">
					<h5 className="text-sm font-semibold text-text">{item.title}</h5>
					<p className="text-sm text-text-meta mt-1">{item.definition}</p>
					{item.examples && item.examples.length > 0 && (
						<div className="mt-2">
							<span className="text-sm font-medium text-text-meta">
								{t("list.signals.examples")}:
							</span>
							<ul className="list-disc list-inside mt-1">
								{item.examples.map((example, index) => (
									<li key={index} className="text-sm text-text-meta italic">
										&ldquo;{example}&rdquo;
									</li>
								))}
							</ul>
						</div>
					)}
				</div>
				<div className="flex gap-2 ml-2">
					<button
						aria-label={t("list.signals.editPriorityItem")}
						className="p-1.5 rounded hover:bg-light-blue text-text-meta hover:text-brand-light"
						type="button"
						onClick={() => onEdit(item)}
					>
						<Icon className="h-4 w-4" icon={faEdit} />
					</button>
					<button
						aria-label={t("list.signals.deletePriorityItem")}
						className="p-1.5 rounded hover:bg-light-blue text-text-meta hover:text-status-error"
						type="button"
						onClick={() => onDelete(item)}
					>
						<Icon className="h-4 w-4" icon={faTrash} />
					</button>
				</div>
			</div>
		</div>
	);
}
